import { VehicleFacadeBirdView } from '@euler/app/flows/report/components/VehicleFacadeBirdView';
import { usePreInspectionResults } from '@euler/app/flows/report/functions/usePreInspectionResults';
import { Img } from '@euler/components/image/AdvancedImage';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useContainerDimensions } from '@euler/functions/useAdaptiveContainerWidth';
import {
  useInventory,
  useInventoryLookup,
} from '@euler/functions/useInventory';
import { AbnormalLevel, SiteInspectionType } from '@euler/model/enum';
import { VehicleReport } from '@euler/model/report';
import { memo, useMemo } from 'react';
import { View } from 'react-native';
import { DashboardImageView } from './DashboardImageView';
import { DashboardSiteItemList } from './DashboardSiteItemList';
import { PreInspectionReportSection as Section } from './PreInspectionReportSection';
import { PreInspectionResultsView } from './PreInspectionResultsView';

export const PreInspectionReportView = memo(
  ({ report, standalone }: { report: VehicleReport; standalone?: boolean }) => {
    const [inventory] = useInventory();
    const inventoryQuery = useInventoryLookup(inventory);
    const { rpx } = useResponsiveContext();

    const { overlays, recordGroups, groupsWithIssues, normalSites } =
      usePreInspectionResults(report.preInspection);

    const { width } = useContainerDimensions();

    const dashboardItems = useMemo(
      () =>
        report.preInspection!.items.filter(
          x =>
            x.inspectionType === SiteInspectionType.Dashboard &&
            x.abnormalLevel !== AbnormalLevel.Fine,
        ),
      [report.preInspection],
    );

    const groups = useMemo(
      () => [...groupsWithIssues, ...recordGroups],
      [groupsWithIssues, recordGroups],
    );

    return (
      <>
        {report.dashboardImgUrl ? (
          <Section
            title="仪表盘信息"
            css={`
              margin-top: ${standalone ? 0 : rpx(10)}px;
            `}
          >
            <View
              css={`
                height: ${(width - rpx(30)) * 0.75}px;
                padding: 0 ${rpx(15)}px;
                padding-bottom: ${rpx(10)}px;
              `}
            >
              <LayoutProviderView
                css={`
                  flex: 1;
                `}
              >
                <DashboardImageView
                  report={report}
                  css={`
                    flex: 1;
                  `}
                />
              </LayoutProviderView>
            </View>
            <DashboardSiteItemList
              items={dashboardItems}
              inventoryQuery={inventoryQuery}
            />
          </Section>
        ) : null}

        <Section title="车辆外观状态">
          <VehicleFacadeBirdView overlays={overlays} size={width - rpx(20)} />
        </Section>

        <PreInspectionResultsView groups={groups} normalSites={normalSites} />

        {report.preInspection?.signatureImgUrl != null && (
          <Section title="客户签名" contentStyle={{ padding: rpx(10) }}>
            <Img
              uri={report.preInspection?.signatureImgUrl}
              resizeMode="contain"
              css={`
                width: 100%;
                aspect-ratio: ${5 / 3};
              `}
            />
          </Section>
        )}
      </>
    );
  },
);
